<script>
import moment from 'moment'
import * as R from 'ramda'

import UserLine from './UserLine'

export default {
  name: 'UserDetails',
  components: {
    UserLine,
  },
  props: {
    userId: { type: Number, required: true },
  },
  data() {
    return {
      ui: {
        disabled: false,
      },
    }
  },
  computed: {
    user() {
      const { $store, userId } = this
      return $store.state.user.userMap &&
        ($store.state.user.userMap[userId] || null)
    },
    displayName() {
      const { $store, userId } = this
      return $store.getters['user/displayName'](userId)
    },
    initials() {
      const { $store, userId } = this
      return $store.getters['user/initials'](userId)
    },
    currentUser() {
      const { $store } = this
      return $store.state.user.current
    },
    isSubordinateOfCurrent() {
      const { currentUser, userId } = this
      return currentUser && currentUser.subordinates.includes(userId)
    },
    showChartLink() {
      const { isSubordinateOfCurrent, currentUser, userId } = this
      return currentUser && (currentUser.id === userId || isSubordinateOfCurrent)
    },
    sortedSubordinateIds() {
      const { $store, user } = this
      return user && R.sortBy(
        $store.getters['user/displayName'],
        user.subordinates,
      )
    },
    sortedChiefIds() {
      const { $store, user } = this
      return user && R.sortBy(
        $store.getters['user/displayName'],
        user.chiefs,
      )
    },
  },
  created() {
    this.$store.dispatch('user/getList', { reload: true })
  },
  methods: {
    todayIso() { return moment().format('YYYY-MM-DD') },
    isSubbed(userId) {
      const { currentUser } = this
      return currentUser && currentUser.follow.includes(userId)
    },
    follow(userId) {
      const { $store, ui } = this
      ui.disabled = true
      $store.dispatch('user/follow', { userId })
        .finally(() => { ui.disabled = false })
    },
    unfollow(userId) {
      const { $store, ui } = this
      ui.disabled = true
      $store.dispatch('user/unfollow', { userId })
        .finally(() => { ui.disabled = false })
    },
  },
}
</script>

<template>
  <div class="UserDetails">
    <VSkeletonLoader
      type="article"
      :loading="user === null"
    >
      <h1 class="mb-4 display-1">
        {{ displayName }}

        <template v-if="user">
          <VTooltip bottom>
            <template #activator="{ on }">
              <VBtn
                icon
                :disabled="ui.disabled"
                @click="isSubbed(user.id)
                  ? unfollow(user.id)
                  : follow(user.id)"
                v-on="on"
              >
                <VIcon
                  :color="isSubbed(user.id)
                    ? 'amber darken-3'
                    : 'grey lighten-1'"
                  v-text="isSubbed(user.id)
                    ? 'mdi-bell-check'
                    : 'mdi-bell'"
                />
              </VBtn>
            </template>
            {{ isSubbed(user.id)
              ? 'Отписаться от уведомлений'
              : 'Подписаться на уведомления' }}
          </VTooltip>
        </template>

        <VBtn
          v-if="showChartLink"
          :to="{ name: 'userChart', query: { user: String(userId), date: todayIso() } }"
          color="primary"
          text
          target="_blank"
        >
          Смотреть события за сегодня
        </VBtn>
      </h1>

      <VRow>
        <VCol>
          <h2 class="headline">
            Подчиненные
          </h2>
          <h3 class="mb-2 caption">
            {{ displayName }} может просматривать события
            следующих пользователей
          </h3>
          <VList
            v-if="user !== null"
            dense
            class="mb-8"
          >
            <UserLine
              v-for="subordinateId in sortedSubordinateIds"
              :key="subordinateId"
              :user-id="subordinateId"
              :disabled="ui.disabled"
              :icon="isSubbed(subordinateId)
                ? 'mdi-bell-check'
                : 'mdi-bell'"
              :icon-color="isSubbed(subordinateId)
                ? 'amber darken-3'
                : 'grey lighten-1'"
              :icon-tooltip="isSubbed(subordinateId)
                ? 'Отписаться от уведомлений'
                : 'Подписаться на уведомления'"
              @click:icon="isSubbed(subordinateId)
                ? unfollow(subordinateId)
                : follow(subordinateId)"
            >
              Подчиненный {{ displayName }}
            </UserLine>
            <VListItem v-if="!sortedSubordinateIds.length">
              <VListItemContent>
                <VListItemSubtitle>
                  нет
                </VListItemSubtitle>
              </VListItemContent>
            </VListItem>
          </VList>
        </VCol>

        <VCol>
          <h2 class="headline">
            Руководители
          </h2>
          <h3 class="mb-2 caption">
            События {{ displayName }} видны следующим пользователям
          </h3>
          <VList
            v-if="user !== null"
            dense
            class="mb-8"
          >
            <UserLine
              v-for="chiefId in sortedChiefIds"
              :key="chiefId"
              :user-id="chiefId"
              :disabled="ui.disabled"
              :icon="isSubbed(chiefId)
                ? 'mdi-bell-check'
                : 'mdi-bell'"
              :icon-color="isSubbed(chiefId)
                ? 'amber darken-3'
                : 'grey lighten-1'"
              :icon-tooltip="isSubbed(chiefId)
                ? 'Отписаться от уведомлений'
                : 'Подписаться на уведомления'"
              @click:icon="isSubbed(chiefId)
                ? unfollow(chiefId)
                : follow(chiefId)"
            >
              Руководитель {{ displayName }}
            </UserLine>
            <VListItem v-if="!sortedChiefIds.length">
              <VListItemContent>
                <VListItemSubtitle>
                  нет
                </VListItemSubtitle>
              </VListItemContent>
            </VListItem>
          </VList>
        </VCol>
      </VRow>
    </VSkeletonLoader>
  </div>
</template>
